import { useState, useCallback, type ElementType } from 'react';
import {
    Box,
    Button,
    Flex,
    Grid,
    GridItem,
    Icon,
    IconButton,
    Text,
} from '@chakra-ui/react';
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb';

const BRAND = '#0071E3';

const startOfDay = (d: Date): Date => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

const sameDay = (a: Date, b: Date): boolean => {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
};

const getMonthGrid = (year: number, month: number): { date: Date; inMonth: boolean }[] => {
    const first = new Date(year, month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { date: Date; inMonth: boolean }[] = [];
    const prevMonthLast = new Date(year, month, 0).getDate();
    for (let i = startPad - 1; i >= 0; i--) {
        cells.push({ date: new Date(year, month - 1, prevMonthLast - i), inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ date: new Date(year, month, d), inMonth: true });
    }
    let next = 1;
    while (cells.length < 42) {
        cells.push({ date: new Date(year, month + 1, next), inMonth: false });
        next += 1;
    }
    return cells;
};

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface SingleDateCalendarProps {
    selectedDate: Date | null;
    minDate?: Date;
    onChange?: (date: Date) => void;
}

const SingleDateCalendar = ({
    selectedDate,
    minDate,
    onChange,
}: SingleDateCalendarProps) => {
    const [viewMonth, setViewMonth] = useState(() => {
        const ref = selectedDate ?? new Date();
        return new Date(ref.getFullYear(), ref.getMonth(), 1);
    });

    const min = minDate ? startOfDay(minDate) : null;

    const handleDayClick = useCallback(
        (raw: Date) => {
            if (!onChange) return;
            const d = startOfDay(raw);
            if (min && d.getTime() < min.getTime()) return;
            onChange(d);
        },
        [min, onChange]
    );

    const navigate = (delta: number) => {
        setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };

    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth();
    const cells = getMonthGrid(y, m);

    return (
        <Box w="100%" maxW="100%" minW={0} overflow="hidden">
            <Flex align="center" justify="space-between" mb={3} w="100%" minW={0}>
                <IconButton
                    aria-label="Previous month"
                    icon={<Icon as={TbChevronLeft as ElementType} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    color="gray.700"
                    _hover={{ bg: '#E6F1FD', color: BRAND }}
                />
                <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                    {monthNames[m]} {y}
                </Text>
                <IconButton
                    aria-label="Next month"
                    icon={<Icon as={TbChevronRight as ElementType} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(1)}
                    color="gray.700"
                    _hover={{ bg: '#E6F1FD', color: BRAND }}
                />
            </Flex>
            <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={0} mb={1} w="100%">
                {dayNames.map((day) => (
                    <GridItem key={day}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" textAlign="center" py={1}>
                            {day}
                        </Text>
                    </GridItem>
                ))}
            </Grid>
            <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={0} w="100%">
                {cells.map(({ date, inMonth }, index) => {
                    if (!inMonth) {
                        return (
                            <GridItem key={`${index}-empty`}>
                                <Box h="34px" />
                            </GridItem>
                        );
                    }

                    const d0 = startOfDay(date);
                    const disabled = min ? d0.getTime() < min.getTime() : false;
                    const isSelected = selectedDate ? sameDay(d0, startOfDay(selectedDate)) : false;

                    let bg = 'transparent';
                    let color = 'gray.800';
                    let fontWeight: 'normal' | 'semibold' = 'normal';
                    let _hover: Record<string, string> = {};

                    if (disabled) {
                        color = 'gray.300';
                    } else if (isSelected) {
                        bg = BRAND;
                        color = 'white';
                        fontWeight = 'semibold';
                        _hover = { bg: '#3544a0', color: 'white' };
                    } else {
                        _hover = { bg: '#E6F1FD', color: BRAND };
                    }

                    return (
                        <GridItem key={`${index}-${d0.getTime()}`}>
                            <Button
                                size="sm"
                                variant="ghost"
                                w="100%"
                                maxW="100%"
                                h="34px"
                                minW={0}
                                p={0}
                                fontSize="sm"
                                fontWeight={fontWeight}
                                isDisabled={disabled}
                                bg={bg}
                                color={color}
                                borderRadius="md"
                                _hover={disabled ? {} : _hover}
                                onClick={() => handleDayClick(date)}
                            >
                                {date.getDate()}
                            </Button>
                        </GridItem>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default SingleDateCalendar;
